import { useState } from "react";
import { motion } from "framer-motion";

import {
  isSupabaseConfigured,
} from "../../lib/supabaseClient";

import {
  createRsvp,
} from "../../services/rsvpService";

import "./RSVPForm.css";


/* =========================================================
   EVENT OPTIONS

   Keep these values exactly the same because they are stored
   in Supabase and used by the Admin dashboard filters.
   ========================================================= */

const eventChoices = [
  {
    value: "Mehendi — Nov 19",
    title: "Tales in Henna",
    ceremony: "Mehendi",
    date: "Thursday, November 19",
    time: "6:00 PM",
  },

  {
    value: "Cocktail — Nov 20",
    title: "Dancing into Forever",
    ceremony: "Cocktail",
    date: "Friday, November 20",
    time: "6:00 PM",
  },

  {
    value: "Haldi — Nov 21",
    title: "Hues of Sunshine",
    ceremony: "Haldi",
    date: "Saturday, November 21",
    time: "9:00 AM",
  },

  {
    value: "Pellikuthuru — Nov 21",
    title: "Bride's Chapter",
    ceremony: "Pellikuthuru",
    date: "Saturday, November 21",
    time: "7:00 PM",
  },

  {
    value: "Pellikoduku — Nov 21",
    title: "Groom's Chapter",
    ceremony: "Pellikoduku",
    date: "Saturday, November 21",
    time: "7:00 PM",
  },

  {
    value: "Wedding — Nov 22",
    title: "Beginning of Forever",
    ceremony: "The Wedding",
    date: "Sunday, November 22",
    time: "Time TBD",
  },
];


const initialForm = {
  guestName: "",
  email: "",
  phone: "",

  attending: "Yes",

  eventsAttending: [],

  adultCount: "1",
  kidsCount: "0",

  vegetarianCount: "1",
  nonVegetarianCount: "0",
  kidsMealCount: "0",
  dietaryNotes: "",

  songRequest: "",

  message: "",

  /*
   * Honeypot.
   * Guests never see this.
   */
  website: "",
};


export default function RSVPForm() {
  const [form, setForm] =
    useState(initialForm);

  const [status, setStatus] =
    useState("idle");

  const [
    errorMessage,
    setErrorMessage,
  ] = useState("");


  const isAttending =
    form.attending === "Yes";


  /* =======================================================
     STANDARD FORM FIELD
     ======================================================= */

  const updateField = (event) => {
    const {
      name,
      value,
    } = event.target;


    setForm((current) => ({
      ...current,

      [name]: value,

      /*
       * If the guest changes to "No",
       * remove any previously selected events.
       */
      ...(name === "attending" &&
      value === "No"
        ? {
            eventsAttending: [],
          }
        : {}),
    }));


    if (status !== "idle") {
      setStatus("idle");
      setErrorMessage("");
    }
  };


  /* =======================================================
     EVENT CHECKBOX
     ======================================================= */

  const toggleEvent = (
    eventValue
  ) => {
    setForm((current) => {
      const alreadySelected =
        current.eventsAttending.includes(
          eventValue
        );


      return {
        ...current,

        eventsAttending:
          alreadySelected
            ? current.eventsAttending.filter(
                (item) =>
                  item !==
                  eventValue
              )
            : [
                ...current.eventsAttending,
                eventValue,
              ],
      };
    });


    if (status !== "idle") {
      setStatus("idle");
      setErrorMessage("");
    }
  };


  /* =======================================================
     SUBMIT
     ======================================================= */

  const submitRSVP = async (
    event
  ) => {
    event.preventDefault();

    setErrorMessage("");


    /*
     * Honeypot against simple bots.
     */
    if (form.website) {
      setStatus("success");

      return;
    }


    /*
     * An attending guest must select
     * at least one celebration.
     */
    if (
      isAttending &&
      form.eventsAttending.length ===
        0
    ) {
      setStatus("error");

      setErrorMessage(
        "Please select at least one celebration you will be joining."
      );

      return;
    }


    /*
     * Optional sanity check:
     * at least one adult when attending.
     */
    if (
      isAttending &&
      Number(form.adultCount) < 1
    ) {
      setStatus("error");

      setErrorMessage(
        "Please enter at least one adult for your party."
      );

      return;
    }


    if (!isSupabaseConfigured) {
      setStatus("error");

      setErrorMessage(
        "RSVP storage is not configured yet. Please add the Supabase values to your .env file."
      );

      return;
    }


    setStatus("sending");


    try {
      await createRsvp(form);

      setStatus("success");

      setForm(initialForm);
    } catch (error) {
      console.error(
        "RSVP submission failed:",
        error
      );

      setStatus("error");

      setErrorMessage(
        error?.message ||
          "We could not save your RSVP right now. Please try again in a moment."
      );
    }
  };


  return (
    <section
      className="rsvpSection"
      id="rsvp"
    >
      <motion.div
        className="rsvpCard"
        initial={{
          opacity: 0,
          y: 45,
        }}
        whileInView={{
          opacity: 1,
          y: 0,
        }}
        viewport={{
          once: true,
          amount: 0.2,
        }}
        transition={{
          duration: 0.7,
          ease: "easeOut",
        }}
      >


        <h2>RSVP</h2>

        <p className="rsvpIntro">
          Tell us who's coming,
          which celebrations we'll
          see you at, and what should
          be waiting on your plate ♡
        </p>


        <form
          className="rsvpForm"
          onSubmit={submitRSVP}
        >
          {/* =================================================
              HONEYPOT
              ================================================= */}

          <label
            className="rsvpHoneypot"
            aria-hidden="true"
          >
            Website

            <input
              type="text"
              name="website"
              value={form.website}
              onChange={updateField}
              tabIndex="-1"
              autoComplete="off"
            />
          </label>


          {/* =================================================
              CONTACT
              ================================================= */}

          <div className="rsvpTwoColumns">
            <label className="rsvpField">
              Your name

              <input
                type="text"
                name="guestName"
                value={
                  form.guestName
                }
                onChange={updateField}
                placeholder="Your name or family name"
                maxLength="120"
                required
              />
            </label>


            <label className="rsvpField">
              Email

              <input
                type="email"
                name="email"
                value={form.email}
                onChange={updateField}
                placeholder="your@email.com"
                maxLength="180"
                required
              />
            </label>
          </div>


          <label className="rsvpField">
            Phone number{" "}
            <span className="optionalText">
              optional
            </span>

            <input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={updateField}
              placeholder="Best number to reach you"
              maxLength="30"
            />
          </label>


          {/* =================================================
              ATTENDING
              ================================================= */}

          <fieldset className="rsvpFieldset">
            <legend>
              Will you be joining us?
            </legend>

            <div className="rsvpOptions">
              <label>
                <input
                  type="radio"
                  name="attending"
                  value="Yes"
                  checked={
                    form.attending ===
                    "Yes"
                  }
                  onChange={
                    updateField
                  }
                />

                Joyfully accepts ♡
              </label>


              <label>
                <input
                  type="radio"
                  name="attending"
                  value="No"
                  checked={
                    form.attending ===
                    "No"
                  }
                  onChange={
                    updateField
                  }
                />

                Regretfully declines
              </label>
            </div>
          </fieldset>


          {/* =================================================
              ONLY SHOW WHEN ATTENDING
              ================================================= */}

          {isAttending && (
            <motion.div
              className="rsvpAttendingFields"
              initial={{
                opacity: 0,
                height: 0,
              }}
              animate={{
                opacity: 1,
                height: "auto",
              }}
              transition={{
                duration: 0.35,
              }}
            >
              {/* =============================================
                  01 — EVENTS
                  ============================================= */}

              <div className="rsvpSectionHeading">
                <span>01</span>

                <div>
                  <h3>
                    Which celebrations
                    will you be joining?
                  </h3>

                  <p>
                    Select every event
                    where we'll get to
                    see you ♡
                  </p>
                </div>
              </div>


              <div className="rsvpEventChoices">
                {eventChoices.map(
                  (eventChoice) => {
                    const selected =
                      form.eventsAttending.includes(
                        eventChoice.value
                      );


                    return (
                      <label
                        key={
                          eventChoice.value
                        }
                        className={`rsvpEventChoice ${
                          selected
                            ? "selected"
                            : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={
                            selected
                          }
                          onChange={() =>
                            toggleEvent(
                              eventChoice.value
                            )
                          }
                        />


                        <span className="eventCheckboxMark">
                          {selected
                            ? "✓"
                            : ""}
                        </span>


                        <span className="eventChoiceText">
                          <small>
                            {
                              eventChoice.ceremony
                            }
                          </small>

                          <strong>
                            {
                              eventChoice.title
                            }
                          </strong>

                          <span>
                            {
                              eventChoice.date
                            }
                            {" · "}
                            {
                              eventChoice.time
                            }
                          </span>
                        </span>
                      </label>
                    );
                  }
                )}
              </div>


              {/* =============================================
                  02 — PARTY
                  ============================================= */}

              <div className="rsvpSectionHeading">
                <span>02</span>

                <div>
                  <h3>
                    Your party
                  </h3>

                  <p>
                    Help us save the
                    right number of
                    seats.
                  </p>
                </div>
              </div>


              <div className="rsvpTwoColumns">
                <label className="rsvpField">
                  Adults

                  <input
                    type="number"
                    name="adultCount"
                    min="1"
                    max="15"
                    value={
                      form.adultCount
                    }
                    onChange={
                      updateField
                    }
                    required
                  />
                </label>


                <label className="rsvpField">
                  Kids

                  <input
                    type="number"
                    name="kidsCount"
                    min="0"
                    max="10"
                    value={
                      form.kidsCount
                    }
                    onChange={
                      updateField
                    }
                  />
                </label>
              </div>


              {/* =============================================
                  03 — FOOD
                  ============================================= */}

              <div className="rsvpSectionHeading">
                <span>03</span>

                <div>
                  <h3>
                    What's on the
                    plate?
                  </h3>

                  <p>
                    Enter meal counts
                    for your whole
                    party.
                  </p>
                </div>
              </div>


              <div className="rsvpThreeColumns">
                <label className="rsvpField">
                  Vegetarian

                  <input
                    type="number"
                    name="vegetarianCount"
                    min="0"
                    max="20"
                    value={
                      form.vegetarianCount
                    }
                    onChange={
                      updateField
                    }
                  />
                </label>


                <label className="rsvpField">
                  Non-vegetarian

                  <input
                    type="number"
                    name="nonVegetarianCount"
                    min="0"
                    max="20"
                    value={
                      form.nonVegetarianCount
                    }
                    onChange={
                      updateField
                    }
                  />
                </label>


                <label className="rsvpField">
                  Kids meals

                  <input
                    type="number"
                    name="kidsMealCount"
                    min="0"
                    max="10"
                    value={
                      form.kidsMealCount
                    }
                    onChange={
                      updateField
                    }
                  />
                </label>
              </div>


              <label className="rsvpField">
                Allergies or dietary
                notes

                <input
                  type="text"
                  name="dietaryNotes"
                  value={
                    form.dietaryNotes
                  }
                  onChange={
                    updateField
                  }
                  placeholder="Jain, vegan, nut allergy, gluten-free..."
                  maxLength="250"
                />
              </label>


              {/* =============================================
                  04 — SONG
                  ============================================= */}

              <div className="rsvpSectionHeading">
                <span>04</span>

                <div>
                  <h3>
                    One for the dance
                    floor
                  </h3>

                  <p>
                    Request a song
                    that'll get you
                    dancing ♫
                  </p>
                </div>
              </div>


              <label className="rsvpField">
                Dance-floor song
                request

                <input
                  type="text"
                  name="songRequest"
                  value={
                    form.songRequest
                  }
                  onChange={
                    updateField
                  }
                  placeholder="What song should we add to the playlist?"
                  maxLength="160"
                />
              </label>
            </motion.div>
          )}


          {/* =================================================
              MESSAGE
              Always visible so declined guests can also send
              a message.
              ================================================= */}

          <label className="rsvpField">
            Message to the couple

            <textarea
              name="message"
              value={form.message}
              onChange={updateField}
              placeholder="A wish, a memory, some marriage advice, or just some love ♡"
              rows="4"
              maxLength="1000"
            />
          </label>


          {/* =================================================
              SUBMIT
              ================================================= */}

          <button
            type="submit"
            className="rsvpButton"
            disabled={
              status === "sending"
            }
          >
            {status === "sending"
              ? "Saving your RSVP..."
              : "Send RSVP ♡"}
          </button>


          {status === "success" && (
            <motion.p
              className="rsvpSuccess"
              initial={{
                opacity: 0,
                y: 8,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
            >
              You're on the list ♡

              Thank you for letting us know.
              We can't wait to celebrate with you!
            </motion.p>
          )}


          {status === "error" && (
            <p className="rsvpError">
              {errorMessage}
            </p>
          )}
        </form>
      </motion.div>
    </section>
  );
}